import React, { useEffect, useState } from 'react';

import { ColDef, GridApi } from 'ag-grid-community';

import { BaseAgGrid } from '../../BaseAgGrid/BaseAgGrid';

/**
 * Props for the MetricsGrid component
 */
export interface MetricsGridProps {
    /** Column definitions for the AG Grid */
    columnDefs: ColDef[];
    /** Array of data rows to display in the grid */
    rowData: any[];
    /** Optional filter text for global filtering */
    filterText?: string;
    /** Callback fired when filters change */
    onFilterChanged?: () => void;
    /** Callback fired when grid is ready */
    onGridReady?: (api: GridApi) => void;
    /** Default sorting configuration */
    defaultSortModel?: { colId: string; sort: 'asc' | 'desc' }[];
    /** Auto group column definition for row grouping */
    autoGroupColumnDef?: ColDef;
    /** Whether all data is fully loaded - controls when to auto-size columns */
    isDataFullyLoaded?: boolean;
    /** the number of row levels to expand by default */
    groupDefaultExpanded?: number;
}

/**
 * MetricsGrid component provides an AG Grid for displaying analytics data
 */
export const MetricsGrid: React.FC<MetricsGridProps> = ({
    columnDefs,
    rowData,
    filterText = '',
    onFilterChanged,
    onGridReady,
    defaultSortModel,
    autoGroupColumnDef,
    isDataFullyLoaded = true,
    groupDefaultExpanded = 0
}) => {
    const [agGridApi, setAgGridApi] = useState<GridApi>();

    // Simple default column definition for AG Grid
    const defaultColDef = {
        // Allow every column to be aggregated
        enableValue: true,
        // Allow every column to be grouped
        enableRowGroup: true,
        // Allow every column to be pivoted
        enablePivot: true,
        // Default column settings
        sortable: true,
        resizable: true,
        floatingFilter: true
    };

    // Simple grid options
    const gridOptions = {
        enableCellTextSelection: true,
        suppressRowClickSelection: true,
        rowBuffer: 20, // Increase row buffer for smoother scrolling
        cacheBlockSize: 100, // Adjust cache block size for better performance
        maxBlocksInCache: 10, // Limit blocks in cache to prevent memory issues
        pagination: true, // Enable pagination to reduce rendering load
        paginationPageSize: 100, // Show 100 rows per page
        suppressAggFuncInHeader: true, // Whether to suppress aggregation function names in headers
        autoGroupColumnDef
    };

    // Simple grid ready handler
    const handleGridReady = (params: { api: GridApi }) => {
        setAgGridApi(params.api);

        if (onGridReady) {
            onGridReady(params.api);
        }
    };

    // Handle first data rendered - apply default sorting when data is first rendered
    const handleFirstDataRendered = (params: { api: GridApi }) => {
        // Apply default sorting if provided
        if (defaultSortModel && defaultSortModel.length > 0) {
            const columnState = defaultSortModel.map((sortItem, index) => ({
                colId: sortItem.colId,
                sort: sortItem.sort,
                sortIndex: index
            }));
            params.api.applyColumnState({ state: columnState });
        }
    };

    // Auto-size columns after grid and data are ready and fully loaded
    useEffect(() => {
        if (agGridApi && rowData && rowData.length > 0 && isDataFullyLoaded) {
            // Add a delay to ensure all rendering and column operations are complete
            const timeoutId = setTimeout(() => {
                try {
                    agGridApi.autoSizeAllColumns();
                } catch (error) {
                    console.warn('Could not auto-size columns:', error);
                }
            }, 0);

            return () => clearTimeout(timeoutId);
        }
    }, [agGridApi, rowData, isDataFullyLoaded]);

    // Simple sidebar configuration
    const sideBar = {
        toolPanels: [
            {
                id: 'columns',
                labelDefault: 'Columns',
                labelKey: 'columns',
                iconKey: 'columns',
                toolPanel: 'agColumnsToolPanel'
            },
            {
                id: 'filters',
                labelDefault: 'Filters',
                labelKey: 'filters',
                iconKey: 'filter',
                toolPanel: 'agFiltersToolPanel'
            }
        ]
    };

    return (
        <div style={{ minHeight: '600px' }}>
            <BaseAgGrid
                columnDefs={columnDefs}
                defaultColDef={defaultColDef}
                rowData={rowData}
                filterText={filterText}
                setAgGridApi={setAgGridApi}
                gridOptions={gridOptions}
                sideBar={sideBar}
                suppressSizeColumnsToFit={true}
                onGridReady={handleGridReady}
                onFirstDataRendered={handleFirstDataRendered}
                onFilterChanged={onFilterChanged}
                groupDefaultExpanded={groupDefaultExpanded}
                statusBar={{ statusPanels: [] }}
                tooltipShowDelay={0}
                tooltipHideDelay={2000}
                loading={!isDataFullyLoaded}
            />
        </div>
    );
};
